import React, { useState, useMemo, useEffect, Fragment, memo } from "react";
import { useTable, useSortBy } from "react-table";
import { FaSortDown, FaSortUp } from "react-icons/fa";
import "react-toastify/dist/ReactToastify.css";
import ApiDataSkeleton from "../CCModeTable/ApiDataSkeleton";
import axios from "axios";
import { NODE_API_URL, BASE_URL } from "../../../../env_config";
import Cookies from "js-cookie";
import { makeStyles, styled } from "@material-ui/core";
import { CcmodeUserInvitationTableColumns } from "./CcmodeUserInvitationTableColumns";
import { toast } from "react-toastify";

const Styles = styled("div")(({ theme }) => ({
    "& th:first-child, & td:first-child": {
        [theme.breakpoints.up("xs")]: {
            paddingLeft: `3rem !important`,
        },
        [theme.breakpoints.up("md")]: {
            paddingLeft: `6rem !important`,
        },
    },
    "& td": {
        [theme.breakpoints.up("xs")]: {
            paddingTop: ".55rem !important",
            paddingBottom: ".55rem !important",
        },
        [theme.breakpoints.up("md")]: {
            paddingTop: ".55rem !important",
            paddingBottom: ".55rem !important",
        },
    },
}));

const useStyles = makeStyles((theme) => ({
    tableRow: {
        fontFamily: 'Rubik,sans-serif !important',
        fontSize: "14px !important",
    },
    mutedRow: {
        background: "hsl(0 0 90%)",
        transition: "background 0.3s ease-in-out",
        "&:hover": {
            background: "hsl(0 0 85%) !important",
        },
    },
    maxContent: {
        width: "max-content",
        whiteSpace: "nowrap",
    },
}));

const CcmodeUserInvitationTable = () => {
    const classes = useStyles();

    const [refreshComponent, setRefreshComponent] = useState(0);
    const [inviteLoading, setInviteLoading] = useState(false);

    const [data, setData] = useState(null);

    function fetchCCModeUsersInvitations(source) {
        axios({
            method: "GET",
            url: `${NODE_API_URL}/INTL/fetch-ccmode-users-invitations`,
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${Cookies.get("cust_auth_token")}`,
            },
            cancelToken: source.token,
        })
            .then((res) => {
                if (res.data.success) {
                    setData(res.data.data);
                }
            })
            .catch((error) => {
                if (error?.response?.data?.message === "UnAuthorized!") {
                    var cookies = document.cookie.split(";");
                    for (var i = 0; i < cookies.length; i++) {
                        if (cookies[i] && !cookies[i].includes("view")) {
                            var equals = cookies[i].indexOf("=");
                            var name =
                                equals > -1
                                    ? cookies[i].substr(0, equals)
                                    : cookies[i];
                            document.cookie =
                                name +
                                "=;expires=Thu, 01 Jan 1970 00:00:00 GMT";
                        }
                    }

                    window.location.replace(`${BASE_URL}/logout`);
                }
                if (error?.response?.data?.message) {
                    toast.error(error?.response?.data?.message);
                }
            });
    }

    function resendInvitation(email) {
        setInviteLoading(true);
        axios({
            method: "POST",
            url: `${NODE_API_URL}/INTL/ccmode-invite/resend`,
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${Cookies.get("cust_auth_token")}`,
            },
            data: { email },
        })
            .then((res) => {
                if (res.data.success) {
                    toast.dismiss();
                    toast.success(res.data.message);
                    setRefreshComponent((prev) => prev + 1);
                }
            })
            .catch((error) => {
                if (error?.response?.data?.message === "UnAuthorized!") {
                    var cookies = document.cookie.split(";");
                    for (var i = 0; i < cookies.length; i++) {
                        if (cookies[i] && !cookies[i].includes("view")) {
                            var equals = cookies[i].indexOf("=");
                            var name =
                                equals > -1
                                    ? cookies[i].substr(0, equals)
                                    : cookies[i];
                            document.cookie =
                                name +
                                "=;expires=Thu, 01 Jan 1970 00:00:00 GMT";
                        }
                    }

                    window.location.replace(`${BASE_URL}/logout`);
                }
                if (error?.response?.data?.message) {
                    toast.error(error?.response?.data?.message);
                }
            })
            .finally(() => setInviteLoading(false));
    }

    useEffect(() => {
        const source = axios.CancelToken.source();

        fetchCCModeUsersInvitations(source);

        return () => {
            source.cancel("Component unmounted: cancel pending request");
        };
    }, [refreshComponent]);

    const columns = useMemo(
        () => CcmodeUserInvitationTableColumns(inviteLoading, resendInvitation),
        [inviteLoading]
    );

    const tableInstance = useTable(
        {
            columns,
            data: data || [],
        },
        useSortBy
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
    } = tableInstance;

    return (
        <>
            {!data ? (
                <ApiDataSkeleton />
            ) : (
                <Styles>
                    <div className="pt-5 pb-0">
                        <div className="table-responsive w-100">
                            <table
                                className="table table-hover"
                                {...getTableProps()}
                            >
                                <thead className="custom_datatable_head">
                                    {headerGroups.map((headerGroup) => (
                                        <tr
                                            {...headerGroup.getHeaderGroupProps()}
                                        >
                                            {headerGroup.headers.map(
                                                (column, j) => (
                                                    <th
                                                        className={`${column.className}`}
                                                        {...column.getHeaderProps(
                                                            column.getSortByToggleProps()
                                                        )}
                                                    >
                                                        {column.render(
                                                            "Header"
                                                        )}
                                                        <span className="text-secondary">
                                                            {column.isSorted ? (
                                                                column.isSortedDesc ? (
                                                                    <FaSortDown />
                                                                ) : (
                                                                    <FaSortUp />
                                                                )
                                                            ) : (
                                                                ""
                                                            )}
                                                        </span>
                                                    </th>
                                                )
                                            )}
                                        </tr>
                                    ))}
                                </thead>
                                <tbody {...getTableBodyProps()}>
                                    {rows.length ? (
                                        rows.map((row, i) => {
                                            prepareRow(row);
                                            return (
                                                <Fragment key={i}>
                                                    <tr
                                                        {...row.getRowProps()}
                                                        className={`${
                                                            row.original
                                                                .is_default_user
                                                                ? classes.mutedRow
                                                                : "py-5"
                                                        } ${
                                                            classes.maxContent
                                                        }`}
                                                    >
                                                        {row.cells.map(
                                                            (cell, i) => {
                                                                return (
                                                                    <td
                                                                        {...cell.getCellProps(
                                                                            {
                                                                                className: `${cell.column.className} ${classes.tableRow} border-bottom`,
                                                                            }
                                                                        )}
                                                                        key={i}
                                                                    >
                                                                        {cell.render(
                                                                            "Cell"
                                                                        )}
                                                                    </td>
                                                                );
                                                            }
                                                        )}
                                                    </tr>
                                                </Fragment>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td
                                                className="text-center bg-gray-200"
                                                colSpan="100%"
                                            >
                                                No Invitations Found
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </Styles>
            )}
        </>
    );
};

export default memo(CcmodeUserInvitationTable);
